import { zodResolver } from '@hookform/resolvers/zod'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import InnerBox from '@repo/ui/inner-box'
import { TFunction } from 'i18next'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z, ZodType } from 'zod'

import useTranslate from '../../../lib/hooks/use-translate'
import { GenderEnum } from '../../../lib/types/enums/common/gender.enum'
import { EmailValidityStatusEnum } from '../../../lib/types/enums/information-requests/email-validity-status.enum'
import {
  OrigineEnum,
  origineEnumMapping
} from '../../../lib/types/enums/information-requests/origin.enum'
import {
  ReceiveBrochureEnum,
  receiveBrochureEnumMapping
} from '../../../lib/types/enums/information-requests/recieve-brochure.enum'
import { InformationRequestInput } from '../../../lib/types/models/information-request/information-request-input'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import DateChip from '../../elements/date-chip'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'
import InformationRequestChildForm from './information-request-child-form'
import InformationRequestContactForm from './information-request-contact-form'
import InformationRequestParentForm from './information-request-parent-form'

const emailOnlyFields = {
  address1: null,
  address2: null,
  city: null,
  postalCode: null,
  country: null,
  phoneNumber: null
} as const

const formSchema: (
  t: TFunction<AvailableLanguages, undefined>
) => ZodType<NullableTransformer<InformationRequestInput>> = (t) => {
  const commonSchema = {
    email: createStringSchema('adresse email', { isEmail: true }),
    origin: createEnumSchema({ fieldName: 'origine', enumType: OrigineEnum }),
    parentGender: createEnumSchema({ fieldName: 'genre', enumType: GenderEnum }),
    parentFirstName: createStringSchema('prénom', {
      errorMessage: 'Veuillez saisir un prénom'
    }),
    parentLastName: createStringSchema('NOM', { errorMessage: 'Veuillez saisir un NOM' }),
    childGender: createEnumSchema({ fieldName: 'genre', enumType: GenderEnum, nullish: true }),
    childFirstName: createStringSchema('prénom', {
      errorMessage: 'Veuillez saisir un prénom'
    }),
    childLastName: createStringSchema('NOM', {
      errorMessage: 'Veuillez saisir un NOM',
      nullish: true
    }),
    address2: createStringSchema("complément d'adresse", { nullish: true }),
    howDidYouHearAboutUsOptionId: createNumberSchema('Comment avez-vous entendu parler de nous')
  }

  return z.discriminatedUnion('receiveBrochureBy', [
    z.object({
      ...commonSchema,
      receiveBrochureBy: z.literal(ReceiveBrochureEnum.EMAIL),
      address1: z.null().optional(),
      city: z.null().optional(),
      postalCode: z.null().optional(),
      country: z.null().optional(),
      phoneNumber: z.null().optional()
    }),
    z.object({
      ...commonSchema,
      receiveBrochureBy: z.literal(ReceiveBrochureEnum.EMAIL_AND_COURIER),
      address1: createStringSchema('adresse'),
      city: createStringSchema('ville'),
      postalCode: createStringSchema('code postal'),
      country: createStringSchema('pays'),
      phoneNumber: PHONE_NUMBER_SCHEMA(t).nullish()
    })
  ])
}

type Props = BaseFormProps<InformationRequestInput, true> & {
  emailValidityStatus?: EmailValidityStatusEnum
}

const InformationRequestForm = ({
  defaultValues,
  emailValidityStatus,
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()
  const form = useForm<InformationRequestInput>({
    resolver: zodResolver(formSchema(t)),
    defaultValues,
    mode: FORM_MODE
  })

  const receiveBrochureBy = useWatch({ control: form.control, name: 'receiveBrochureBy' })
  const isEmailOnly = receiveBrochureBy === ReceiveBrochureEnum.EMAIL
  const isEmailAndCourier = receiveBrochureBy === ReceiveBrochureEnum.EMAIL_AND_COURIER

  useEffect(() => {
    if (!isEmailOnly) return

    Object.entries(emailOnlyFields).forEach(([field, value]) => {
      form.setValue(field as keyof typeof emailOnlyFields, value, { shouldValidate: true })
    })
  }, [form, isEmailOnly])

  const handleSubmit = (values: InformationRequestInput) => {
    const submitAction = getSubmitAction(validationButtonProps.buttonsConfig)

    submitAction({
      ...values,
      ...(values.receiveBrochureBy === ReceiveBrochureEnum.EMAIL ? emailOnlyFields : {})
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex items-center gap-3 mb-4">
          <DateChip date={defaultValues?.createdAt ?? new Date()} label="Créé" />
          <DateChip date={defaultValues?.updatedAt ?? new Date()} label="Mise à jour" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* First Column */}
          <div className="flex flex-col gap-4">
            <InnerBox className="bg-primary-light border-0" title="Origine">
              <FormField
                control={form.control}
                name="origin"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        label="Origine"
                        labelClassName="bg-primary-light"
                        options={Object.values(OrigineEnum).map((status) => ({
                          label: origineEnumMapping[status].label,
                          value: status
                        }))}
                        selectedOption={getSelectedEnumOption(field.value, origineEnumMapping)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </InnerBox>

            <InformationRequestParentForm required />

            <InformationRequestChildForm required />
          </div>

          {/* Second Column */}
          <div className="flex flex-col gap-4">
            <InformationRequestContactForm
              emailValidityStatus={emailValidityStatus}
              required={isEmailAndCourier}
              hideAddressFields={isEmailOnly}
            />

            <InnerBox title={'Choix du format'}>
              <div className={'flex flex-col gap-3'}>
                {Object.values(ReceiveBrochureEnum).map((value) => (
                  <FormField
                    key={value}
                    control={form.control}
                    name={'receiveBrochureBy'}
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormControl>
                            <ControlledRadioInput
                              {...field}
                              label={receiveBrochureEnumMapping[value]}
                              checked={field.value === value}
                              onCheck={() => field.onChange(value)}
                            />
                          </FormControl>
                        </FormItem>
                      )
                    }}
                  />
                ))}
              </div>
            </InnerBox>
          </div>
        </div>

        <div className="mb-4" />

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default InformationRequestForm
